import React, { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { BottomNav } from '../components/BottomNav';
import { ChevronLeft, Bell, MoreHorizontal, Home, MessageCircle, Calendar, TrendingUp, AlertCircle, Package, UserPlus, CheckCircle, Clock, XCircle, UserCheck, Building2, Check, X } from 'lucide-react';
interface NotificationsPageProps {
  onBack?: () => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onCalendarClick?: () => void;
  onChatClick?: () => void;
  onProfileClick?: () => void;
}
type NotificationCategory = 'all' | 'unread';
interface Notification {
  id: number;
  type: 'client' | 'property' | 'rmi' | 'calendar' | 'system' | 'inventory' | 'developer-request';
  title: string;
  message: string;
  time: string;
  isRead: boolean;
  avatar?: string;
  actionLabel?: string;
  developerRequest?: {
    developerId: number;
    developerName: string;
    tier: 'gold' | 'silver' | 'bronze';
    status: 'pending' | 'accepted' | 'declined';
  };
}
export function NotificationsPage({
  onBack,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onCalendarClick,
  onChatClick,
  onProfileClick
}: NotificationsPageProps) {
  const [activeCategory, setActiveCategory] = useState<NotificationCategory>('all');
  const [notifications, setNotifications] = useState<Notification[]>([{
    id: 1,
    type: 'developer-request',
    title: 'Accreditation request',
    message: 'Palm Hills Developments wants to accredit you as a Gold Partner',
    time: '1h',
    isRead: false,
    developerRequest: {
      developerId: 1,
      developerName: 'Palm Hills Developments',
      tier: 'gold',
      status: 'pending'
    }
  }, {
    id: 2,
    type: 'developer-request',
    title: 'Accreditation request',
    message: 'Sodic wants to accredit you as a Silver Partner',
    time: '2h',
    isRead: false,
    developerRequest: {
      developerId: 2,
      developerName: 'Sodic',
      tier: 'silver',
      status: 'pending'
    }
  }, {
    id: 3,
    type: 'client',
    title: 'New client added',
    message: 'Ahmed Hamdy Mohamed has been added to your client list',
    time: '2h',
    isRead: false,
    avatar: 'AH',
    actionLabel: 'View Profile'
  }, {
    id: 4,
    type: 'rmi',
    title: 'RMI request approved',
    message: 'Your RMI request for Madinety - 2BR Apartment has been approved by the developer',
    time: '3h',
    isRead: false,
    actionLabel: 'View Details'
  }, {
    id: 5,
    type: 'client',
    title: 'Client status updated',
    message: 'Sarah Ahmed moved to "Negotiating" stage',
    time: '5h',
    isRead: true,
    avatar: 'SA'
  }, {
    id: 6,
    type: 'property',
    title: 'New property matches',
    message: "3 new properties match Ahmed Hamdy's search criteria in New Cairo",
    time: '1d',
    isRead: false,
    actionLabel: 'View Properties'
  }, {
    id: 7,
    type: 'rmi',
    title: 'RMI response received',
    message: 'Palm Hills responded to your RMI for Hyde Park Villa',
    time: '1d',
    isRead: true
  }, {
    id: 8,
    type: 'calendar',
    title: 'Upcoming meeting',
    message: 'Site visit with Karim Mahmoud scheduled for tomorrow at 10:00 AM',
    time: '1d',
    isRead: false,
    actionLabel: 'View Calendar'
  }, {
    id: 9,
    type: 'system',
    title: 'Congratulations! 🎉',
    message: 'You have been accredited as a Gold Partner with Palm Hills Developments',
    time: '2d',
    isRead: true
  }, {
    id: 10,
    type: 'inventory',
    title: 'Price update',
    message: 'Prices updated for 5 properties in Madinety compound',
    time: '2d',
    isRead: true
  }, {
    id: 11,
    type: 'rmi',
    title: 'RMI request pending',
    message: 'Your RMI request for New Cairo Penthouse is awaiting developer approval',
    time: '3d',
    isRead: true
  }, {
    id: 12,
    type: 'system',
    title: 'New features available',
    message: 'Check out enhanced search filters and client analytics dashboard',
    time: '3d',
    isRead: true
  }]);
  const [showOptionsMenu, setShowOptionsMenu] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredNotifications = activeCategory === 'unread' ? notifications.filter(n => !n.isRead) : notifications;
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const handleMarkAsRead = (id: number) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      isRead: true
    } : n));
    setShowOptionsMenu(null);
  };
  const handleMarkAsUnread = (id: number) => {
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      isRead: false
    } : n));
    setShowOptionsMenu(null);
  };
  const handleDeleteNotification = (id: number) => {
    setNotifications(notifications.filter(n => n.id !== id));
    setShowOptionsMenu(null);
  };
  const handleAcceptRequest = (id: number) => {
    setNotifications(notifications.map(n => n.id === id && n.developerRequest ? {
      ...n,
      isRead: true,
      developerRequest: {
        ...n.developerRequest,
        status: 'accepted'
      }
    } : n));
  };
  const handleDeclineRequest = (id: number) => {
    setNotifications(notifications.map(n => n.id === id && n.developerRequest ? {
      ...n,
      isRead: true,
      developerRequest: {
        ...n.developerRequest,
        status: 'declined'
      }
    } : n));
  };
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'developer-request':
        return {
          icon: Building2,
          color: 'bg-yellow-500'
        };
      case 'client':
        return {
          icon: UserPlus,
          color: 'bg-blue-500'
        };
      case 'property':
        return {
          icon: Home,
          color: 'bg-green-500'
        };
      case 'rmi':
        return {
          icon: MessageCircle,
          color: 'bg-purple-500'
        };
      case 'calendar':
        return {
          icon: Calendar,
          color: 'bg-orange-500'
        };
      case 'inventory':
        return {
          icon: Package,
          color: 'bg-teal-500'
        };
      case 'system':
        return {
          icon: AlertCircle,
          color: 'bg-gray-500'
        };
    }
  };
  const getNotificationBgColor = (type: Notification['type']) => {
    switch (type) {
      case 'developer-request':
        return 'from-yellow-50 to-yellow-100/50';
      case 'client':
        return 'from-blue-50 to-blue-100/50';
      case 'property':
        return 'from-green-50 to-green-100/50';
      case 'rmi':
        return 'from-purple-50 to-purple-100/50';
      case 'calendar':
        return 'from-orange-50 to-orange-100/50';
      case 'inventory':
        return 'from-teal-50 to-teal-100/50';
      case 'system':
        return 'from-gray-50 to-gray-100/50';
    }
  };
  const getTierBadgeColor = (tier: 'gold' | 'silver' | 'bronze') => {
    switch (tier) {
      case 'gold':
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
      case 'silver':
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
      case 'bronze':
        return 'bg-gradient-to-r from-orange-600 to-orange-700 text-white';
    }
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-2085">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-2086">
        {/* Header */}
        <div className="bg-white px-4 py-3 sticky top-0 z-10 border-b border-gray-100" data-id="element-2087">
          <div className="flex items-center justify-between mb-3" data-id="element-2088">
            <div className="flex items-center gap-3" data-id="element-2089">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2090">
                <ChevronLeft className="w-6 h-6 text-neutral-text" data-id="element-2091" />
              </button>
              <h1 className="text-2xl font-bold text-neutral-text" data-id="element-2092">
                Notifications
              </h1>
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2093">
              <MoreHorizontal className="w-6 h-6 text-gray-600" data-id="element-2094" />
            </button>
          </div>

          {/* Category Tabs - Facebook Style */}
          <div className="flex gap-2" data-id="element-2095">
            <button onClick={() => setActiveCategory('all')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${activeCategory === 'all' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2096">
              All
            </button>
            <button onClick={() => setActiveCategory('unread')} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors relative ${activeCategory === 'unread' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2097">
              Unread
              {unreadCount > 0 && <span className="ml-1.5 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full" data-id="element-2098">
                  {unreadCount}
                </span>}
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-gray-100" data-id="element-2099">
          {filteredNotifications.length > 0 ? filteredNotifications.map(notification => {
          const {
            icon: Icon,
            color
          } = getNotificationIcon(notification.type);
          const bgGradient = getNotificationBgColor(notification.type);
          return <div key={notification.id} className={`relative transition-colors hover:bg-gray-50 ${!notification.isRead ? 'bg-blue-50/30' : 'bg-white'}`} data-id="element-2100">
                  <div className="px-4 py-3 flex items-start gap-3" data-id="element-2101">
                    {/* Avatar/Icon */}
                    <div className="relative flex-shrink-0" data-id="element-2102">
                      {notification.avatar ? <div className="w-14 h-14 rounded-full bg-gradient-to-br from-secondary to-secondary-dark flex items-center justify-center text-white font-bold text-lg" data-id="element-2103">
                          {notification.avatar}
                        </div> : <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${bgGradient} flex items-center justify-center`} data-id="element-2104">
                          <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center`} data-id="element-2105">
                            <Icon className="w-5 h-5 text-white" data-id="element-2106" />
                          </div>
                        </div>}
                      {!notification.isRead && <div className="absolute top-0 right-0 w-3 h-3 bg-blue-600 rounded-full border-2 border-white" data-id="element-2107" />}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-2108">
                      <div className="flex items-start justify-between gap-2 mb-1" data-id="element-2109">
                        <p className="text-sm text-neutral-text leading-snug" data-id="element-2110">
                          <span className="font-semibold" data-id="element-2111">
                            {notification.title}
                          </span>{' '}
                          <span className="text-gray-600" data-id="element-2112">
                            {notification.message}
                          </span>
                        </p>
                        <button onClick={() => setShowOptionsMenu(showOptionsMenu === notification.id ? null : notification.id)} className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0" data-id="element-2113">
                          <MoreHorizontal className="w-5 h-5 text-gray-500" data-id="element-2114" />
                        </button>
                      </div>

                      <div className="flex items-center gap-2 mb-2" data-id="element-2115">
                        <span className="text-xs font-medium text-blue-600" data-id="element-2116">
                          {notification.time}
                        </span>
                        {!notification.isRead && <div className="w-1 h-1 bg-gray-400 rounded-full" data-id="element-2117" />}
                      </div>

                      {/* Developer Request Actions */}
                      {notification.type === 'developer-request' && notification.developerRequest && <div className="mt-3" data-id="element-2118">
                            {notification.developerRequest.status === 'pending' ? <div className="space-y-2" data-id="element-2119">
                                <div className="flex items-center gap-2 mb-2" data-id="element-2120">
                                  <span className="text-xs font-semibold text-gray-600" data-id="element-2121">
                                    Partnership Tier:
                                  </span>
                                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${getTierBadgeColor(notification.developerRequest.tier)}`} data-id="element-2122">
                                    {notification.developerRequest.tier.toUpperCase()}
                                  </span>
                                </div>
                                <div className="flex gap-2" data-id="element-2123">
                                  <button onClick={() => handleAcceptRequest(notification.id)} className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-2124">
                                    <Check className="w-4 h-4" data-id="element-2125" />
                                    Accept
                                  </button>
                                  <button onClick={() => handleDeclineRequest(notification.id)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-2126">
                                    <X className="w-4 h-4" data-id="element-2127" />
                                    Decline
                                  </button>
                                </div>
                              </div> : notification.developerRequest.status === 'accepted' ? <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-2128">
                                <CheckCircle className="w-4 h-4 text-green-600" data-id="element-2129" />
                                <span className="text-sm font-semibold text-green-700" data-id="element-2130">
                                  Request accepted
                                </span>
                              </div> : <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-2131">
                                <XCircle className="w-4 h-4 text-gray-600" data-id="element-2132" />
                                <span className="text-sm font-semibold text-gray-700" data-id="element-2133">
                                  Request declined
                                </span>
                              </div>}
                          </div>}

                      {notification.actionLabel && notification.type !== 'developer-request' && <button className="text-sm font-semibold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors" data-id="element-2134">
                            {notification.actionLabel}
                          </button>}
                    </div>
                  </div>

                  {/* Options Menu */}
                  {showOptionsMenu === notification.id && <div className="absolute right-4 top-16 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20 min-w-[200px]" data-id="element-2135">
                      {!notification.isRead ? <button onClick={() => handleMarkAsRead(notification.id)} className="w-full px-4 py-2.5 text-left text-sm text-neutral-text hover:bg-gray-50 flex items-center gap-3" data-id="element-2136">
                          <CheckCircle className="w-4 h-4 text-gray-500" data-id="element-2137" />
                          Mark as read
                        </button> : <button onClick={() => handleMarkAsUnread(notification.id)} className="w-full px-4 py-2.5 text-left text-sm text-neutral-text hover:bg-gray-50 flex items-center gap-3" data-id="element-2138">
                          <Clock className="w-4 h-4 text-gray-500" data-id="element-2139" />
                          Mark as unread
                        </button>}
                      <button onClick={() => handleDeleteNotification(notification.id)} className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3" data-id="element-2140">
                        <XCircle className="w-4 h-4" data-id="element-2141" />
                        Remove notification
                      </button>
                    </div>}
                </div>;
        }) : <div className="flex flex-col items-center justify-center py-20 px-4" data-id="element-2142">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-2143">
                <Bell className="w-12 h-12 text-gray-400" data-id="element-2144" />
              </div>
              <h3 className="text-xl font-bold text-neutral-text mb-2" data-id="element-2145">
                No notifications
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs" data-id="element-2146">
                {activeCategory === 'unread' ? "You're all caught up! No unread notifications at the moment." : "You don't have any notifications yet."}
              </p>
            </div>}
        </div>

        {/* Click outside to close menu */}
        {showOptionsMenu !== null && <div className="fixed inset-0 z-10" onClick={() => setShowOptionsMenu(null)} data-id="element-2147" />}

        <BottomNav activeNav="notifications" onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onNotificationsClick={() => {}} onProfileClick={onProfileClick} data-id="element-2148" />
      </div>
    </div>;
}